import React, { useState, useMemo } from 'react';
import { Modal, Drawer, Tooltip, Popover } from 'antd';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, X } from 'lucide-react';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import { useNavigate } from 'react-router-dom';
import '../styles/calendarPopup.css';

dayjs.extend(isBetween);

export default function CalendarPopup({
  visible,
  onClose,
  mode = 'leave', // 'leave' | 'request' | 'attendance'
  holidays = [],
  leaves = [],
  requests = [],
  attendance = [],
}) {
  const [currentDate, setCurrentDate] = useState(dayjs());
  const [selectedDate, setSelectedDate] = useState(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const navigate = useNavigate();

  const prevMonth = () => setCurrentDate(currentDate.subtract(1, 'month'));
  const nextMonth = () => setCurrentDate(currentDate.add(1, 'month'));
  const goToday = () => setCurrentDate(dayjs());

  // --- Data Mapping ---
  // Returns all events for a specific dayjs date
  const getEventsForDate = (date) => {
    const dateStr = date.format('YYYY-MM-DD');
    let dayEvents = [];

    // 1. Holidays
    const dayHolidays = holidays.filter(
      (h) => (h.holiday_date && h.holiday_date.startsWith(dateStr)) || (h.date && h.date.startsWith(dateStr))
    );
    dayHolidays.forEach(h => {
      dayEvents.push({ type: 'holiday', title: h.name || h.holiday_name, data: h });
    });

    // 2. Leaves (includes both 'leaves' array and 'requests' array if they are leave requests)
    const allLeaves = mode === 'request' ? requests.filter(r => r.request_type === 'Leave') : leaves;
    allLeaves.forEach(l => {
      const start = dayjs(l.start_date || l.fromDate);
      const end = dayjs(l.end_date || l.toDate);
      if (date.isBetween(start, end, 'day', '[]')) {
        const status = (l.status || 'PENDING').toUpperCase();
        let evType = 'pending';
        if (status.includes('APPROVED')) evType = 'approved';
        if (status === 'REJECTED' || status === 'CANCELLED') evType = 'rejected';
        
        dayEvents.push({ type: 'leave', statusType: evType, title: `Leave: ${l.leave_type_name || l.leaveType || 'Leave'}`, data: l });
      }
    });

    // 3. Other Requests (for Employee Request page)
    if (mode === 'request') {
      const nonLeaves = requests.filter(r => r.request_type !== 'Leave');
      nonLeaves.forEach(req => {
        const start = dayjs(req.start_date || req.created_at);
        const end = dayjs(req.end_date || req.created_at);
        if (date.isBetween(start, end, 'day', '[]')) {
          const status = (req.status || 'PENDING').toUpperCase();
          let evType = 'pending';
          if (status.includes('APPROVED')) evType = 'approved';
          if (status === 'REJECTED' || status === 'CANCELLED') evType = 'rejected';
          if (req.request_type === 'Attendance Regularization') evType = 'regularization';
          
          dayEvents.push({ type: 'request', statusType: evType, title: req.request_type, data: req });
        }
      });
    }

    // 4. Attendance (for Attendance Report page)
    if (mode === 'attendance') {
      const attRecord = attendance.find(a => (a.date || a.dayDate) === dateStr);
      if (attRecord) {
        const status = String(attRecord.status || attRecord.status_value).toUpperCase();
        if (status === 'P') dayEvents.push({ type: 'attendance', statusType: 'present', title: 'Present', data: attRecord });
        else if (status === 'A') dayEvents.push({ type: 'attendance', statusType: 'absent', title: 'Absent', data: attRecord });
        else if (status === 'HD') dayEvents.push({ type: 'attendance', statusType: 'present', title: 'Half Day', data: attRecord });
      }
    }

    // 5. Weekends
    if (date.day() === 0 || date.day() === 6) {
      if (dayEvents.length === 0) {
        dayEvents.push({ type: 'weekoff', title: 'Week Off' });
      }
    }

    return dayEvents;
  };

  const renderEventPopoverContent = (ev, d) => {
    return (
      <div className="cal-popover-content" style={{ minWidth: 220, fontSize: '13px' }}>
        <h4 style={{ margin: '0 0 10px 0', fontSize: '15px', color: '#1e293b' }}>{ev.title}</h4>
        
        {ev.type === 'holiday' && (
          <>
            <div style={{ marginBottom: 6 }}><span style={{ color: '#64748b', marginRight: 8 }}>Date:</span>{dayjs(ev.data.holiday_date || ev.data.date).format('DD MMM YYYY')}</div>
            <div style={{ marginBottom: 6 }}><span style={{ color: '#64748b', marginRight: 8 }}>Type:</span>{ev.data.type || 'Public Holiday'}</div>
          </>
        )}
        
        {(ev.type === 'leave' || ev.type === 'request') && ev.data && (
          <>
            <div style={{ marginBottom: 6 }}><span style={{ color: '#64748b', marginRight: 8 }}>Employee:</span>{ev.data.employee_name || ev.data.employeeId || 'Self'}</div>
            <div style={{ marginBottom: 6 }}><span style={{ color: '#64748b', marginRight: 8 }}>Time:</span>{dayjs(ev.data.start_date || ev.data.fromDate).format('DD MMM YYYY')} - {dayjs(ev.data.end_date || ev.data.toDate).format('DD MMM YYYY')}</div>
            {ev.data.status && <div style={{ marginBottom: 6 }}><span style={{ color: '#64748b', marginRight: 8 }}>Status:</span>{ev.data.status.replace(/_/g, ' ')}</div>}
            {ev.data.reason && <div style={{ marginBottom: 6 }}><span style={{ color: '#64748b', marginRight: 8 }}>Reason:</span>{ev.data.reason}</div>}
            {ev.data.approved_by_name && <div style={{ marginBottom: 6 }}><span style={{ color: '#64748b', marginRight: 8 }}>Approved By:</span>{ev.data.approved_by_name}</div>}
          </>
        )}

        {ev.type === 'attendance' && ev.data && (
          <>
            <div style={{ marginBottom: 6 }}><span style={{ color: '#64748b', marginRight: 8 }}>Date:</span>{d.format('DD MMM YYYY')}</div>
            <div style={{ marginBottom: 6 }}><span style={{ color: '#64748b', marginRight: 8 }}>In Time:</span>{ev.data.checkIn || ev.data.check_in_time || '--'}</div>
            <div style={{ marginBottom: 6 }}><span style={{ color: '#64748b', marginRight: 8 }}>Out Time:</span>{ev.data.checkOut || ev.data.check_out_time || '--'}</div>
            <div style={{ marginBottom: 6 }}><span style={{ color: '#64748b', marginRight: 8 }}>Total Hrs:</span>{ev.data.total_hours || '--'}</div>
          </>
        )}
      </div>
    );
  };

  // --- Render Calendar Grid ---
  const renderCalendar = () => {
    const startOfMonth = currentDate.startOf('month');
    const endOfMonth = currentDate.endOf('month');
    const startDate = startOfMonth.startOf('week');
    const endDate = endOfMonth.endOf('week');

    const days = [];
    let day = startDate;
    while (day.isBefore(endDate)) {
      days.push(day);
      day = day.add(1, 'day');
    }

    return (
      <div className="cal-grid-wrapper">
        <div className="cal-grid-header">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
            <div key={d} className="cal-day-name">{d}</div>
          ))}
        </div>
        <div className="cal-grid">
          {days.map((d, i) => {
            const isCurrentMonth = d.month() === currentDate.month();
            const isToday = d.isSame(dayjs(), 'day');
            const isWeekend = d.day() === 0 || d.day() === 6;
            const events = isCurrentMonth ? getEventsForDate(d) : [];

            return (
              <div
                key={i}
                className={`cal-cell ${!isCurrentMonth ? 'empty' : ''} ${isToday ? 'today' : ''} ${isWeekend ? 'weekend' : ''}`}
                onClick={() => {
                  if (isCurrentMonth) {
                    if (mode === 'leave') {
                      onClose && onClose();
                      navigate('/LeaveForm', { state: { selectedDate: d.format('YYYY-MM-DD') } });
                    } else if (mode === 'attendance' || mode === 'request') {
                      onClose && onClose();
                      navigate('/employee-request', { state: { selectedDate: d.format('YYYY-MM-DD') } });
                    } else {
                      setSelectedDate(d);
                      setDrawerVisible(true);
                    }
                  }
                }}
              >
                {isCurrentMonth && (
                  <>
                    <span className="cal-date-number">{d.date()}</span>
                    <div className="cal-events-container">
                      {events.map((ev, idx) => {
                        let dotClass = ev.type === 'holiday' ? 'holiday' 
                                     : ev.type === 'weekoff' ? 'weekoff'
                                     : ev.statusType;
                        return (
                          <div 
                            key={idx}
                            onClick={(e) => e.stopPropagation()} 
                            style={{ width: '100%' }}
                          >
                            <Popover 
                              content={renderEventPopoverContent(ev, d)} 
                              trigger="click"
                              placement="right"
                            >
                              <div className={`cal-event-badge ${dotClass}`} style={{ cursor: 'pointer' }}>{ev.title}</div>
                            </Popover>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // --- Render Drawer Details ---
  const renderDrawerContent = () => {
    if (!selectedDate) return null;
    const events = getEventsForDate(selectedDate);
    
    if (events.length === 0 || (events.length === 1 && events[0].type === 'weekoff')) {
      return (
        <div className="cal-no-records">
          No records found for {selectedDate.format('DD MMM YYYY')}
        </div>
      );
    }

    return (
      <div className="cal-drawer-content">
        {events.map((ev, idx) => {
          if (ev.type === 'weekoff') return null;
          
          let cardClass = ev.type === 'holiday' ? 'holiday' : ev.statusType;
          let badgeClass = cardClass;

          return (
            <div key={idx} className={`cal-record-card ${cardClass}`}>
              <div className="cal-record-header">
                <h4 className="cal-record-title">{ev.title}</h4>
                {ev.type !== 'holiday' && ev.data?.status && (
                  <span className={`cal-record-badge ${badgeClass}`}>
                    {ev.data.status.replace(/_/g, ' ')}
                  </span>
                )}
              </div>

              {ev.type === 'holiday' && (
                <>
                  <div className="cal-record-detail"><span className="cal-record-label">Date</span><span className="cal-record-value">{dayjs(ev.data.holiday_date || ev.data.date).format('DD MMM YYYY')}</span></div>
                  <div className="cal-record-detail"><span className="cal-record-label">Type</span><span className="cal-record-value">{ev.data.type || 'Public Holiday'}</span></div>
                </>
              )}

              {(ev.type === 'leave' || ev.type === 'request') && ev.data && (
                <>
                  <div className="cal-record-detail"><span className="cal-record-label">Employee</span><span className="cal-record-value">{ev.data.employee_name || ev.data.employeeId || 'Self'}</span></div>
                  <div className="cal-record-detail"><span className="cal-record-label">From</span><span className="cal-record-value">{dayjs(ev.data.start_date || ev.data.fromDate).format('DD MMM YYYY')}</span></div>
                  <div className="cal-record-detail"><span className="cal-record-label">To</span><span className="cal-record-value">{dayjs(ev.data.end_date || ev.data.toDate).format('DD MMM YYYY')}</span></div>
                  {ev.data.reason && <div className="cal-record-detail"><span className="cal-record-label">Reason</span><span className="cal-record-value">{ev.data.reason}</span></div>}
                  {ev.data.approved_by_name && <div className="cal-record-detail"><span className="cal-record-label">Approved By</span><span className="cal-record-value">{ev.data.approved_by_name}</span></div>}
                </>
              )}

              {ev.type === 'attendance' && ev.data && (
                <>
                  <div className="cal-record-detail"><span className="cal-record-label">Date</span><span className="cal-record-value">{selectedDate.format('DD MMM YYYY')}</span></div>
                  <div className="cal-record-detail"><span className="cal-record-label">In Time</span><span className="cal-record-value">{ev.data.checkIn || ev.data.check_in_time || '--'}</span></div>
                  <div className="cal-record-detail"><span className="cal-record-label">Out Time</span><span className="cal-record-value">{ev.data.checkOut || ev.data.check_out_time || '--'}</span></div>
                  <div className="cal-record-detail"><span className="cal-record-label">Total Hrs</span><span className="cal-record-value">{ev.data.total_hours || '--'}</span></div>
                </>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <Modal
        title={null}
        open={visible}
        onCancel={onClose}
        footer={null}
        width={900}
        wrapClassName="cal-popup-modal"
        closable={false}
      >
        <div className="cal-header-top">
          <h2 className="cal-header-title">{currentDate.format('MMMM YYYY')}</h2>
          <div className="cal-nav-buttons">
            <button className="cal-today-btn" onClick={goToday}>Today</button>
            <button className="cal-nav-btn" onClick={prevMonth}><ChevronLeft size={18} /></button>
            <button className="cal-nav-btn" onClick={nextMonth}><ChevronRight size={18} /></button>
            <button className="cal-nav-btn" onClick={onClose} style={{ marginLeft: '12px' }}><X size={18} /></button>
          </div>
        </div>

        <div className="cal-legend">
          <div className="cal-legend-item"><div className="cal-dot approved"></div> Approved</div>
          <div className="cal-legend-item"><div className="cal-dot pending"></div> Pending</div>
          <div className="cal-legend-item"><div className="cal-dot rejected"></div> Rejected</div>
          <div className="cal-legend-item"><div className="cal-dot holiday"></div> Holiday</div>
          <div className="cal-legend-item"><div className="cal-dot regularization"></div> Regularization</div>
          {mode === 'attendance' && (
             <>
               <div className="cal-legend-item"><div className="cal-dot present"></div> Present</div>
               <div className="cal-legend-item"><div className="cal-dot absent"></div> Absent</div>
             </>
          )}
          <div className="cal-legend-item"><div className="cal-dot weekoff"></div> Week Off</div>
        </div>

        {renderCalendar()}
      </Modal>

      <Drawer
        title={selectedDate ? selectedDate.format('dddd, DD MMM YYYY') : 'Details'}
        placement="right"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        width={400}
        className="cal-drawer"
      >
        {renderDrawerContent()}
      </Drawer>
    </>
  );
}
